import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    CatalogType,
    CredentialType,
    DashboardContextType,
    ResourceType,
    ResourceVersionType,
    SearchItem,
    UrlSearchBuilder,
    UserRolType,
} from '../types'
import { URLAPP, URLQUERY } from '../config'
import matchHelper from '../helpers/match.helper'

interface UseSearchItemGeneratorProps {
    dashboardContext: DashboardContextType | null,
    minSearchLength: number
}

export default function useSearchItemGenerator({ dashboardContext, minSearchLength }: UseSearchItemGeneratorProps) {
    const [search, setSearch] = useState('')
    const [matchItems, setMatchItems] = useState<SearchItem[]>([])
    const pathsData = dashboardContext?.appData['paths']
    const menuData = dashboardContext?.appData['menu']
    const aforeList = dashboardContext?.getCatalog('AFO')
    const getItemIcon = useCallback((urlBase: string) => menuData?.find(m => m.path?.includes(urlBase))?.icon || 'file-alt', [menuData])

    const repositoryMatchUrlBuilder = useMemo((): UrlSearchBuilder<CredentialType> => (item) => ({
        link: `${URLAPP.workspaceRepository}?${URLQUERY.target}=${item.id}`,
        icon: getItemIcon(URLAPP.workspace)
    }), [getItemIcon])

    const catalogMatchUrlBuilder = useMemo((): UrlSearchBuilder<CatalogType> => (item, urlParam) => ({
        link: `${URLAPP.workspaceCatalog}?${URLQUERY.target}=${urlParam}&${URLQUERY.status}=${item.active ? 1 : 0}`,
        icon: getItemIcon(URLAPP.workspace)
    }), [getItemIcon])

    const restMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceType> => (item, urlParam) => ({
        link: pathsData?.includes(URLAPP.apidocRestAdmin) ?
            `${URLAPP.apidocRestAdmin}?${URLQUERY.group}=${urlParam}&${URLQUERY.target}=${item.name}` :
            `${URLAPP.apidocRestDoc}/${item.name}`,
        icon: getItemIcon(URLAPP.apidoc)
    }), [pathsData, getItemIcon])

    const soapMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceType> => (item, urlParam) => ({
        link: pathsData?.includes(URLAPP.apidocSoapAdmin) ?
            `${URLAPP.apidocSoapAdmin}?${URLQUERY.group}=${urlParam}&${URLQUERY.target}=${item.id}` :
            `${URLAPP.apidocSoapDoc}/${item.name}`,
        icon: getItemIcon(URLAPP.apidoc)
    }), [pathsData, getItemIcon])

    const sdkMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceType> => (item, urlParam) => ({
        link: pathsData?.includes(URLAPP.mobileSdkAdmin) ?
            `${URLAPP.mobileSdkAdmin}?${URLQUERY.tab}=${urlParam}&${URLQUERY.normative}=${item.versions[0]?.normative}&${URLQUERY.target}=${item.versions[0]?.id}` :
            `${URLAPP.mobileSdkList}?${URLQUERY.tab}=${urlParam}&${URLQUERY.normative}=${item.versions[0]?.normative}&${URLQUERY.target}=${item.versions[0]?.id}`,
        icon: getItemIcon(URLAPP.mobile)
    }), [pathsData, getItemIcon])

    const sdkVersionMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceVersionType> => (version, urlParam) => ({
        link: pathsData?.includes(URLAPP.mobileSdkAdmin) ?
            `${URLAPP.mobileSdkAdmin}?${URLQUERY.tab}=${urlParam}&${URLQUERY.normative}=${version.normative}&${URLQUERY.target}=${version.id}` :
            `${URLAPP.mobileSdkList}?${URLQUERY.tab}=${urlParam}&${URLQUERY.normative}=${version.normative}&${URLQUERY.target}=${version.id}`,
        icon: getItemIcon(URLAPP.mobile)
    }), [pathsData, getItemIcon])

    const rolMenuMatchUrlBuilder = useMemo((): UrlSearchBuilder<UserRolType> => (item) => ({
        link: `${URLAPP.accessMenu}?${URLQUERY.group}=${item.id}`,
        icon: getItemIcon(URLAPP.linkedResources)
    }), [pathsData, getItemIcon])

    const linkedResourceMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceType> => (item, urlPath) => ({
        link: `${urlPath}?${URLQUERY.target}=${item.id}`,
        icon: getItemIcon(URLAPP.linkedResources)
    }), [pathsData, getItemIcon])

    const linkedResourceAdminMatchUrlBuilder = useMemo((): UrlSearchBuilder<ResourceType> => (item) => ({
        link: `${URLAPP.accessResources}?${URLQUERY.target}=${item.id}`,
        icon: getItemIcon(URLAPP.linkedResources)
    }), [pathsData, getItemIcon])


    useEffect(() => {
        if (search.length >= minSearchLength) {
            let items = [
                ...matchHelper.searchInRest(search, restMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData['rest']),
                ...matchHelper.searchInSoap(search, soapMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData['soap']),
                ...matchHelper.searchInResource(search, sdkMatchUrlBuilder, sdkVersionMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData['sdk'], aforeList),
                ...matchHelper.searchPageMatches(search, matchHelper.matchParamsDefault, dashboardContext?.appData.menu || [])
            ]
            if (pathsData?.includes(URLAPP.workspace)) {
                items = [
                    ...matchHelper.searchInRepository(search, repositoryMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData['credentials']),
                    ...matchHelper.searchInCatalog(search, catalogMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData.catalogApp),
                    ...matchHelper.searchLinkedResourceAdmin(search, linkedResourceAdminMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData.linkedResources, dashboardContext?.appData.roles),
                    ...matchHelper.searchRol(search, rolMenuMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData.roles),
                    ...items
                ]
            } else {
                items = [
                    ...matchHelper.searchLinkedResource(search, linkedResourceMatchUrlBuilder, matchHelper.matchParamsDefault, dashboardContext?.appData.linkedResources, dashboardContext?.appData.menu),
                    ...items
                ]
            }
            setMatchItems(items)
        } else {
            setMatchItems([])
        }
    }, [search])
    return {
        search,
        setSearch,
        matchItems
    }
}
